// React e Hooks
import React, { useState, useEffect, useMemo } from 'react';

// Material UI - Componentes Básicos
import {
  Typography,
  Box,
  TextField,
  MenuItem,
  CircularProgress,
  Button,
  Tab,
  Tabs,
} from '@mui/material';

// Material UI - Tema
import { ThemeProvider } from '@mui/material/styles';

// Componentes de Layout
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import WelcomeHeader from '../components/WelcomeHeader';

// Componentes Funcionais
import ModoEspiao from '../components/ModoEspiao';
import ContentAgentTable from '../components/Table/ContentAgentTable'; // NOVO

// Componentes de Visualização
import MessagesChart from '../components/Charts/MessagesChart';

// Bibliotecas de Terceiros
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

/**
 * Configuração do tema customizado para o aplicativo
 * Define cores primárias, secundárias, cor de fundo e tipografia
 */
import theme from '../components/theme/theme';

import PageContainer from '../components/Layout/PageContainer';

// Token Validation Components
import { useTokenValidation, TokenExpiredDialog } from '../utils/tokenValidation';

import ACConfigTab from '../components/Tabs/AC-Config';

/**
 * Componente principal do Assistente Pós-Venda
 * Gerencia dashboard com métricas, tabela de gestão e modo espião
 */
const SuporteConteudo = () => {
  // ========== VALIDAÇÃO DE TOKEN ==========
  const { showExpiredDialog, countdown, redirectToHome } = useTokenValidation();

  // ===== ESTADOS LOCAIS =====

  // Estado para controle de abas
  const [tabIndex, setTabIndex] = useState(0);

  // Estados para o Modo Espião
  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [loadingContacts, setLoadingContacts] = useState(false);

  // Estados específicos para os filtros do Agente de Conteúdo
  const [contentAgentData, setContentAgentData] = useState([]);
  const [filteredContentAgentData, setFilteredContentAgentData] = useState([]);
  const [loadingContentAgent, setLoadingContentAgent] = useState(false);
  const [nomeFilter, setNomeFilter] = useState('');

  // Estados para os filtros de data Dashboard
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Estados para os filtros de data Gestão
  const [startDateGestao, setStartDateGestao] = useState('');
  const [endDateGestao, setEndDateGestao] = useState('');

  const [isTabLoading,] = useState(false);

  // URL base da API, obtida das variáveis de ambiente
  const baseURL = process.env.REACT_APP_API_BASE_URL;

  // Estados para dados do dashboard
  const [dashboardData, setDashboardData] = useState({ data: [], labels: [] });
  const [loadingDashboard, setLoadingDashboard] = useState(false);

  const [forceTokenExpired, setForceTokenExpired] = useState(false);

  /***
   * Estilos padronizados para o componente de filtro dos usuários
  */

  const gap = 24; // em px (gap md: 3 = theme.spacing(3) = 24px)
  const totalComponents = 5; // 4 TextFields + 1 Button
  const containerWidth = 100; // % base
  const minWidthPerComponent = `calc(${containerWidth / totalComponents}% - ${(gap * (totalComponents - 1)) / totalComponents}px)`;

  /**
   * Manipula o clique em um WhatsAppId na tabela de atendimentos
   * Seleciona o contato e redireciona para a aba Modo Espião
   * @param {Object} atendimento - Objeto contendo dados do atendimento clicado
   */
  const handleWhatsAppIdClick = (usuario) => {
    // Adaptar o objeto para o formato esperado pelo ModoEspiao
    const contactFormatted = {
      id: usuario.id,
      whatsAppId: usuario.celular, // Mapear celular para whatsAppId
      nome: usuario.nome,
      celular: usuario.celular,
      dataHoraCadastroAtualizado: usuario.dataHoraCadastroAtualizado,
      whatsAppName: usuario.whatsAppName
    };

    setSelectedContact(contactFormatted);
    setTabIndex(modoEspiaoTabIndex); // Muda para a aba Modo Espião (índice 2)
  };

  /**
   * Hook personalizado que gerencia o estado e as operações relacionadas aos atendimentos
   * Inclui lógica de carregamento, filtragem e manipulação de filtros
   * 
   * NOTA: Para demonstração, estou usando dados placeholder aqui
   */
  const useAtendimentosPlaceholder = () => {
    const [dateFilter, setDateFilter] = useState('Mês');
    const [situacaoFilter, setSituacaoFilter] = useState('');
    const [whatsAppIdFilter, setWhatsAppIdFilter] = useState('');
    const [dataFilter, setDataFilter] = useState('');
    const [loading,] = useState(false);

    const handleClearFilters = () => {
      setDateFilter('Mês');
      setSituacaoFilter('');
      setWhatsAppIdFilter('');
      setDataFilter('');
    };

    return {
      atendimentos: [],
      filteredAtendimentos: [],
      loading,
      dateFilter,
      setDateFilter,
      situacaoFilter,
      setSituacaoFilter,
      whatsAppIdFilter,
      setWhatsAppIdFilter,
      dataFilter,
      setDataFilter,
      handleClearFilters
    };
  };


  const {
    dateFilter,            // Filtro de data selecionado
    setDateFilter,         // Função para atualizar filtro de data
    whatsAppIdFilter,      // Filtro de WhatsApp ID
    setWhatsAppIdFilter,   // Função para atualizar filtro de WhatsApp ID
  } = useAtendimentosPlaceholder(); // Usando dados placeholder

  /**
   * Função para limpar os filtros específicos do Agente de Conteúdo
   */
  const handleClearContentAgentFilters = () => {
    setWhatsAppIdFilter('');
    setNomeFilter('');
    setStartDateGestao('');
    setEndDateGestao('');
  };

  const gestaoTabIndex = 1;
  const modoEspiaoTabIndex = 2;

  /**
 * Effect para carregar dados do dashboard quando a aba Dashboard estiver ativa
 */
  useEffect(() => {
    if (tabIndex === 0) {
      setLoadingDashboard(true);

      const fetchDashboardData = async () => {
        try {
          const token = localStorage.getItem('jwtToken');

          if (token) {
            const decodedToken = jwtDecode(token);
            const idClienteAipim = decodedToken.id_cliente;

            // Formatar datas para o formato esperado pelo backend (YYYY-MM-DD)
            const formatDate = (date) => {
              return date ? new Date(date).toISOString().split('T')[0] : '';
            };

            // Determinar agrupamento baseado no filtro
            const agrupamento = dateFilter;

            const params = {
              idClienteAipim,
              agrupamento,
              ...(startDate && { dataInicio: formatDate(startDate) }),
              ...(endDate && { dataFim: formatDate(endDate) })
            };

            const response = await axios.get(`${baseURL}/produto/AC/mensagens-resumo`, {
              params,
              headers: { Authorization: `Bearer ${token}` }
            });

            // Processar dados para o formato do gráfico
            const processedData = processChartData(response.data, dateFilter);
            setDashboardData(processedData);
          } else {
            console.error("Token não encontrado no localStorage.");
          }
        } catch (error) {
          if (
            error.response?.status === 401 ||
            error.response?.data?.message?.toLowerCase().includes("token") // ou "expired"
          ) {
            setForceTokenExpired(true); // Força abrir o diálogo
          }
          console.error("Erro ao buscar dados do dashboard:", error);
        }

        setLoadingDashboard(false);
      };

      fetchDashboardData();
    }
  }, [tabIndex, baseURL, startDate, endDate, dateFilter]);

  /**
   * Função para processar dados da API para o formato do gráfico
   */
  const processChartData = (data, filter) => {
    // Verificação de segurança
    if (!data || !Array.isArray(data) || data.length === 0) {
      return { data: [], labels: [] };
    }

    let chartData = [];
    let labels = [];

    if (filter === 'Dia') {
      chartData = data.map(item => item.totalMensagens);
      labels = data.map(item => {
        return `${item.dia}/${item.mes}/${item.ano}`
      });
    } else if (filter === 'Mês') {
      chartData = data.map(item => item.totalMensagens);
      labels = data.map(item => {
        const meses = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
          'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
        return `${meses[item.mes - 1]} ${item.ano}`;
      });
    } else if (filter === 'Ano') {
      chartData = data.map(item => item.totalMensagens);
      labels = data.map(item => item.ano.toString());
    }

    return { data: chartData, labels };
  };

  /**
   * Effect para carregar dados do Agente de Conteúdo quando a aba de Gestão estiver ativa
   * ATUALIZADO: Usando dados placeholder para demonstração
   */
  useEffect(() => {
    if (tabIndex === gestaoTabIndex) {
      setLoadingContentAgent(true);

      const fetchContentAgentData = async () => {
        try {
          const token = localStorage.getItem('jwtToken');

          if (token) {
            const decodedToken = jwtDecode(token);
            const idClienteAipim = decodedToken.id_cliente;

            // Formatar datas para o formato esperado pelo backend (YYYY-MM-DD)
            const formatDate = (date) => {
              return date ? new Date(date).toISOString().split('T')[0] : '';
            };

            const params = {
              idCliente: idClienteAipim,
              ...(startDateGestao && { dataInicio: formatDate(startDateGestao) }),
              ...(endDateGestao && { dataFim: formatDate(endDateGestao) })
            };

            const response = await axios.get(`${baseURL}/produto/AC/usuarios-cliente`, {
              params,
              headers: { Authorization: `Bearer ${token}` }
            });

            // Ordenar pela dataHoraCadastroAtualizado (do mais recente para o mais antigo)
            const sortedData = response.data.sort((a, b) => {
              return new Date(b.dataHoraCadastroAtualizado) - new Date(a.dataHoraCadastroAtualizado);
            });

            setContentAgentData(sortedData);
          } else {
            console.error("Token não encontrado no localStorage.");
          }
        } catch (error) {
          if (
            error.response?.status === 401 ||
            error.response?.data?.message?.toLowerCase().includes("token") // ou "expired"
          ) {
            setForceTokenExpired(true); // Força abrir o diálogo
          }
          console.error("Erro ao buscar dados do agente de conteúdo:", error);
        }

        setLoadingContentAgent(false);
      };

      fetchContentAgentData();
    }
  }, [tabIndex, baseURL, startDateGestao, endDateGestao, gestaoTabIndex]);


  /**
   * Effect para filtrar os dados do Agente de Conteúdo baseado nos filtros aplicados
   */
  useEffect(() => {
    let filtered = [...contentAgentData];

    // Filtro por WhatsApp ID (ajustar campo conforme modelo de dados)
    if (whatsAppIdFilter.trim()) {
      filtered = filtered.filter(item =>
        (item.celular || '').toLowerCase().includes(whatsAppIdFilter.toLowerCase())
      );
    }

    // Filtro por Nome
    if (nomeFilter.trim()) {
      filtered = filtered.filter(item =>
        (item.nome || '').toLowerCase().includes(nomeFilter.toLowerCase())
      );
    }

    setFilteredContentAgentData(filtered);
  }, [contentAgentData, whatsAppIdFilter, nomeFilter, dateFilter]);

  /**
   * Effect para buscar contatos para o Modo Espião quando esta aba estiver ativa
   * Carrega a lista de contatos da API apenas quando necessário
   */
  useEffect(() => {
    if (tabIndex === modoEspiaoTabIndex) {
      setLoadingContacts(true);

      const fetchContacts = async () => {
        try {
          // Recupera o token JWT do localStorage
          const token = localStorage.getItem('jwtToken');

          if (token) {
            // Decodifica o token para obter o ID do cliente
            const decodedToken = jwtDecode(token);
            const idClienteAipim = decodedToken.id_cliente;

            // Faz requisição à API para buscar a lista de contatos
            const response = await axios.get(`${baseURL}/produto/AC/lista-contatos`, {
              params: { idClienteAipim },
              headers: { Authorization: `Bearer ${token}` }
            });

            // Atualiza o estado com os contatos recebidos
            setContacts(response.data);
          } else {
            console.error("Token não encontrado no localStorage.");
          }
        } catch (error) {
          if (
            error.response?.status === 401 ||
            error.response?.data?.message?.toLowerCase().includes("token") // ou "expired"
          ) {
            setForceTokenExpired(true); // Força abrir o diálogo
          }
          console.error("Erro ao buscar contatos.");
        }

        // Finaliza o indicador de carregamento
        setLoadingContacts(false);
      };

      fetchContacts();
    }
  }, [tabIndex, baseURL, modoEspiaoTabIndex]); // Executa quando a aba muda ou o baseURL é alterado

  const aggregatedByPeriod = useMemo(() => {
    if (!dashboardData || !dashboardData.data) {
      return { data: [], labels: [] };
    }
    return dashboardData;
  }, [dashboardData]);

  const handleClearFilters = () => {
    setStartDate('')
    setEndDate('')
    setDateFilter('Mês');
  }
  // Definição das abas
  const tabs = [
    {
      label: "Dashboard",
      content: (
        <Box sx={{ mt: 3 }}>
          {/* Container para os filtros de data */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Seletor de filtro por período */}
            <TextField
              select
              label="Agrupar por"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              variant="outlined"
              size="small"
              sx={{
                bgcolor: 'background.paper',
                borderRadius: 1,
                minWidth: 180,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1.5,
                  color: 'text.primary',
                  '& fieldset': {
                    borderColor: 'divider',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#667eea',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: 'text.secondary',
                },
                '& .MuiSelect-icon': {
                  color: 'text.secondary',
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  opacity: 1,
                },
              }}
            >
              <MenuItem value="Dia">Dia</MenuItem>
              <MenuItem value="Mês">Mês</MenuItem>
              <MenuItem value="Ano">Ano</MenuItem>
            </TextField>

            {/* Campo Data de Início */}
            <TextField
              type="date"
              label="Data de Início"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              variant="outlined"
              size="small"
              error={startDate && endDate && startDate > endDate}
              helperText={startDate && endDate && startDate > endDate ? "Data de início não pode ser posterior à data de fim" : ""}
              inputProps={{
                max: endDate || undefined
              }}
              InputLabelProps={{
                shrink: true,
              }}
              sx={{
                bgcolor: 'background.paper',
                borderRadius: 1,
                minWidth: 180,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1.5,
                  color: 'text.primary',
                  '& fieldset': {
                    borderColor: 'divider',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#667eea',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: 'text.secondary',
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  opacity: 1,
                },
              }}
            />

            {/* Campo Data de Fim */}
            <TextField
              type="date"
              label="Data de Fim"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              variant="outlined"
              size="small"
              error={startDate && endDate && endDate < startDate}
              helperText={startDate && endDate && endDate < startDate ? "Data de fim não pode ser anterior à data de início" : ""}
              inputProps={{
                min: startDate || undefined
              }}
              InputLabelProps={{
                shrink: true,
              }}
              sx={{
                bgcolor: 'background.paper',
                borderRadius: 1,
                minWidth: 180,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1.5,
                  color: 'text.primary',
                  '& fieldset': {
                    borderColor: 'divider',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#667eea',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: 'text.secondary',
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  opacity: 1,
                },
              }}
            />

            <Button
              variant="outlined"
              onClick={handleClearFilters}
              sx={{
                borderColor: 'primary.main',
                color: 'primary.main',
                bgcolor: (theme) =>
                  theme.palette.mode === 'dark'
                    ? 'rgba(42, 133, 226, 0.12)'
                    : 'transparent',
                fontWeight: 600,
                borderRadius: 1.5,
                textTransform: 'none',
                fontSize: '0.875rem',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                minWidth: 180,
                '&:hover': {
                  borderColor: 'primary.light',
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'dark'
                      ? 'rgba(42, 133, 226, 0.22)'
                      : 'rgba(102, 126, 234, 0.08)',
                  boxShadow: '0 6px 20px rgba(102, 126, 234, 0.2)',
                  transform: 'translateY(-1px)',
                },
                '&:active': {
                  transform: 'translateY(0px)',
                  boxShadow: '0 2px 8px rgba(102, 126, 234, 0.18)',
                },
              }}
            >
              Limpar Filtros
            </Button>
          </Box>

          {/* Indicador de carregamento ou gráfico */}
          {aggregatedByPeriod.data.length === 0 ? (
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: 400,
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: 1,
              mt: 3,
              maxWidth: '100%'
            }}>
              <Typography variant="body1" sx={{ ml: 2 }}>
                {loadingDashboard ? 'Carregando...' : 'Nenhum resultado encontrado'}
              </Typography>
            </Box>
          ) : (
            <MessagesChart
              data={aggregatedByPeriod.data || []}
              labels={aggregatedByPeriod.labels || []}
              dateFilter={dateFilter}
              title="Distribuição de Mensagens"
              totalMensagens={(aggregatedByPeriod.data || []).reduce((sum, value) => sum + (value || 0), 0)}
            />
          )}
        </Box>
      ),
    },
    {
      label: "Gestão",
      content: (
        <Box sx={{ mt: 3 }}>
          {/* Painel de filtros */}
          <Box sx={{
            bgcolor: 'background.paper',
            p: { xs: 2, sm: 3 },
            borderRadius: 2,
            boxShadow: {
              xs: '0 2px 10px rgba(0,0,0,0.08)',
              md: '0 4px 20px rgba(0,0,0,0.08)'
            },
            border: '1px solid',
            borderColor: 'divider',
            mb: 3
          }}>
            {/* Layout responsivo dos filtros */}
            <Box sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: { xs: 2, md: 3 },
              alignItems: { xs: 'stretch', md: 'flex-end' }
            }}>
              {/* Campo Data de Início da aba Gestão */}
              <TextField
                type="date"
                label="Data de Início"
                value={startDateGestao}
                onChange={(e) => setStartDateGestao(e.target.value)}
                variant="outlined"
                size="small"
                error={startDateGestao && endDateGestao && startDateGestao > endDateGestao}
                helperText={startDateGestao && endDateGestao && startDateGestao > endDateGestao ? "Data de início não pode ser posterior à data de fim" : ""}
                inputProps={{
                  max: endDateGestao || undefined
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{
                  bgcolor: 'background.paper',
                  borderRadius: 1,
                  minWidth: minWidthPerComponent,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5,
                    color: 'text.primary',
                    '& fieldset': {
                      borderColor: 'divider',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#667eea',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: 'text.secondary',
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    opacity: 1,
                  },
                }}
              />

              {/* Campo Data de Fim da aba Gestão */}
              <TextField
                type="date"
                label="Data de Fim"
                value={endDateGestao}
                onChange={(e) => setEndDateGestao(e.target.value)}
                variant="outlined"
                size="small"
                error={startDateGestao && endDateGestao && endDateGestao < startDateGestao}
                helperText={startDateGestao && endDateGestao && endDateGestao < startDateGestao ? "Data de fim não pode ser anterior à data de início" : ""}
                inputProps={{
                  min: startDateGestao || undefined
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{
                  bgcolor: 'background.paper',
                  borderRadius: 1,
                  minWidth: minWidthPerComponent,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5,
                    color: 'text.primary',
                    '& fieldset': {
                      borderColor: 'divider',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#667eea',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: 'text.secondary',
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    opacity: 1,
                  },
                }}
              />

              {/* Campo WhatsApp ID */}
              <TextField
                label="WhatsApp ID"
                placeholder="Digite o WhatsApp ID"
                value={whatsAppIdFilter}
                onChange={(e) => setWhatsAppIdFilter(e.target.value)}
                variant="outlined"
                size="small"
                InputLabelProps={{
                  shrink: whatsAppIdFilter ? true : false
                }}
                sx={{
                  minWidth: minWidthPerComponent,
                  bgcolor: 'background.paper',
                  borderRadius: 1.5,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5,
                    color: 'text.primary',
                    '& fieldset': {
                      borderColor: 'divider',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#667eea',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: 'text.secondary',
                  },
                  '& .MuiInputBase-input': {
                    color: 'text.primary',
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    opacity: 1,
                  },
                }}
              />

              {/* Campo Nome */}
              <TextField
                label="Nome"
                placeholder="Digite o nome"
                value={nomeFilter}
                onChange={(e) => setNomeFilter(e.target.value)}
                variant="outlined"
                size="small"
                InputLabelProps={{
                  shrink: nomeFilter ? true : false
                }}
                sx={{
                  minWidth: minWidthPerComponent,
                  bgcolor: 'background.paper',
                  borderRadius: 1.5,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5,
                    color: 'text.primary',
                    '& fieldset': {
                      borderColor: 'divider',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#667eea',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: 'text.secondary',
                  },
                  '& .MuiInputBase-input': {
                    color: 'text.primary',
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    opacity: 1,
                  },
                }}
              />

              {/* Botão Limpar Filtros */}
              <Button
                variant="outlined"
                onClick={handleClearContentAgentFilters}
                sx={{
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  bgcolor: (theme) =>
                    theme.palette.mode === 'dark'
                      ? 'rgba(42, 133, 226, 0.12)'
                      : 'transparent',
                  fontWeight: 600,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  minWidth: minWidthPerComponent,
                  '&:hover': {
                    borderColor: 'primary.light',
                    backgroundColor: (theme) =>
                      theme.palette.mode === 'dark'
                        ? 'rgba(42, 133, 226, 0.22)'
                        : 'rgba(102, 126, 234, 0.08)',
                    boxShadow: '0 6px 20px rgba(102, 126, 234, 0.2)',
                    transform: 'translateY(-1px)',
                  },
                  '&:active': {
                    transform: 'translateY(0px)',
                    boxShadow: '0 2px 8px rgba(102, 126, 234, 0.18)',
                  },
                }}
              >
                Limpar Filtros
              </Button>
            </Box>
          </Box>

          {/* Indicador de carregamento ou tabela RESPONSIVA */}
          {loadingContentAgent ? (
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: { xs: 200, sm: 300 },
              flexDirection: { xs: 'column', sm: 'row' }
            }}>
              <CircularProgress />
              <Typography
                variant="body1"
                sx={{
                  ml: { xs: 0, sm: 2 },
                  mt: { xs: 2, sm: 0 },
                  textAlign: 'center',
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }}
              >
                Carregando dados do agente de conteúdo...
              </Typography>
            </Box>
          ) : (
            <Box sx={{
              overflow: 'auto'
            }}>
              {/* Container com scroll horizontal para mobile */}
              <Box sx={{
                overflow: 'auto',
                '&::-webkit-scrollbar': {
                  height: 8,
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: 'background.default',
                  borderRadius: 10,
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#4b5570' : '#c1c1c1',
                  borderRadius: 10,
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#6b7693' : '#a8a8a8',
                }
              }}>
                <ContentAgentTable
                  data={filteredContentAgentData}
                  onWhatsAppClick={handleWhatsAppIdClick}
                  loading={loadingContentAgent}
                  sx={{
                    minWidth: { xs: 600, sm: 'auto' }, // Garante largura mínima no mobile
                    '& .MuiTableCell-root': {
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      padding: { xs: '8px 4px', sm: '16px' },
                      whiteSpace: { xs: 'nowrap', sm: 'normal' }
                    },
                    '& .MuiTableHead-root .MuiTableCell-root': {
                      fontWeight: 600,
                      backgroundColor: 'background.default'
                    }
                  }}
                />
              </Box>
            </Box>
          )}
        </Box>
      ),
    },
    {
      label: "Modo Espião",
      content: (
        <Box>
          <Box
            sx={{
              bgcolor: 'rgba(102, 126, 234, 0.05)',
              borderRadius: 2,
              border: '1px solid rgba(102, 126, 234, 0.1)',
              overflow: 'hidden',
            }}
          >
            <ModoEspiao
              contacts={contacts}
              selectedContact={selectedContact}
              onSelectContact={setSelectedContact}
              currentTab="outras-abas"
              endpointPrefix="AC"
              loadingContacts={loadingContacts}
              setForceTokenExpired={setForceTokenExpired}
            />
          </Box>
        </Box>
      ),
    },
    ...[
        {
          label: "Configuração",
          content: (
            <Box sx={{ mt: 3 }}>
              <ACConfigTab produto="AC" />
            </Box>
          ),
        },
      ],
  ];

  return (
    <ThemeProvider theme={theme}>
      {/* Layout principal com design responsivo */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' }, // Coluna em mobile, linha em desktop
          minHeight: '100vh',
          width: '100vw',
          maxWidth: '100%',
          position: 'relative',
          overflow: 'hidden',
          bgcolor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >

        {/* Sidebar responsivo */}
        <Sidebar
          aria-label="Menu de navegação lateral"
          sx={{
            width: { xs: '100%', md: 280 },
            flexShrink: 0,
            order: { xs: 2, md: 1 },
          }}
        />

        {/* Área de conteúdo principal */}
        <Box
          sx={{
            flexGrow: 1,
            order: { xs: 1, md: 2 },
            width: { xs: '100%', md: 'auto' },
          }}
        >
          {/* Cabeçalho responsivo */}
          <Header
            title="Agente de Conteúdo"
            aria-label="Agente de Conteúdo"
            sx={{
              px: { xs: 2, sm: 3, md: 4 },
              py: { xs: 1.5, md: 2 },
            }}
          />

          {/* Container principal do conteúdo */}
          <PageContainer
            maxWidth={false}
            sx={{
              width: '100%',
              maxWidth: '100%',
              px: { xs: 1.5, sm: 2, md: 3 },
              py: { xs: 1.25, md: 1.5 },
            }}
          >
            {/* Header de boas-vindas com gradiente */}
            <WelcomeHeader
              title='Agente de Conteúdo'
              subtitle=''
            />

            {/* Abas de navegacao */}
            <Box
              sx={{
                bgcolor: 'background.paper',
                borderRadius: { xs: 2, md: 3 },
                border: '1px solid',
                borderColor: 'divider',
                px: { xs: 1, md: 1.2 },
                mb: { xs: 2, md: 2.4 },
              }}
            >
              {/* Renderizar labels dinamicamente */}
              <Tabs
                value={tabIndex}
                onChange={(_, newValue) => setTabIndex(newValue)}
                variant="scrollable"
                sx={{
                  minHeight: 48,
                  '& .MuiTab-root': {
                    minHeight: 48,
                    textTransform: 'none',
                    fontSize: { xs: '0.8rem', sm: '0.9rem' },
                    fontWeight: 700,
                    minWidth: { xs: 120, sm: 140 },
                    px: { xs: 1.5, sm: 2 }
                  }
                }}
              >
                {tabs.map((tab, index) => (
                  <Tab key={index} label={tab.label} />
                ))}
              </Tabs>
            </Box>

            {/* Container principal com abas */}
            <Box
              sx={{
                bgcolor: 'background.paper',
                borderRadius: { xs: 2, md: 3 },
                boxShadow: {
                  xs: '0 2px 10px rgba(0,0,0,0.08)',
                  md: '0 4px 20px rgba(0,0,0,0.08)'
                },
                border: '1px solid',
                borderColor: 'divider',
                mb: { xs: 2, md: 0 },
                overflow: 'hidden',
              }}
            >
              <Box sx={{ p: { xs: 2, sm: 3, md: 3 }, pb: { xs: 6, sm: 3, md: 3 } }}>
                {isTabLoading ? (
                  <Box sx={{ mt: 6, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <CircularProgress size={40} thickness={4} color="primary" />
                    <Box sx={{ mt: 2, fontSize: '1rem', color: '#666' }}>
                      Carregando...
                    </Box>
                  </Box>
                ) : (
                  <>
                    {/* Renderizar conteúdo dinamicamente */}
                    {tabs[tabIndex].content}
                  </>
                )}
              </Box>
            </Box>
          </PageContainer>
        </Box>
      </Box>
      {/* ========== DIALOG DE TOKEN EXPIRADO - GLOBAL ========== */}
      {/* 
        POSICIONADO AQUI para aparecer independente da aba ativa
        Este dialog ficará sempre no nível mais alto do componente
      */}
      <TokenExpiredDialog
        open={showExpiredDialog || forceTokenExpired}
        countdown={countdown}
        onRedirect={redirectToHome}
      />
    </ThemeProvider>
  );
};

export default SuporteConteudo;
